import { useState } from "react";
import { BRAND } from "@/lib/brand";
import { formatFriendlyDate, formatHours } from "@/lib/dateUtils";
import { Pencil, Trash2, Clock, Edit3, Send } from "lucide-react";
import { toast } from "sonner";
import EditEntryDialog from "@/components/EditEntryDialog";
import StatusBadge, {
  entryStatus,
  isEntryEditable,
  isEntrySubmittable,
} from "@/components/StatusBadge";
import { api } from "@/lib/api";

export default function EntriesTable({
  entries,
  onChanged,
  showUserName = false,
  readOnly = false,
  previewMode = false,
  onPreviewUpdate,
  onPreviewDelete,
}) {
  const [editing, setEditing] = useState(null);

  const handleSubmit = async (entry) => {
    if (!entry) return;
    if (previewMode) {
      // Submit doesn't really apply in preview (entries default to auto-approved
      // anyway), but we honor the click rather than silently ignoring it.
      toast.info("Preview mode — submit/approve workflow not simulated.");
      return;
    }
    try {
      await api.post(`/entries/${entry.id}/submit`);
      toast.success("Entry submitted for review");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit entry.");
    }
  };

  const handleDelete = async (entry) => {
    if (!entry) return;
    if (previewMode) {
      onPreviewDelete?.(entry.id);
      return;
    }
    try {
      await api.delete(`/entries/${entry.id}`);
      onChanged?.();
      toast.success("Entry deleted", {
        description: `${formatHours(entry.hours)} · ${formatFriendlyDate(entry.date)}`,
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/entries/${entry.id}/restore`);
              toast.success("Entry restored");
              onChanged?.();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Could not restore entry.");
            }
          },
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete entry.");
    }
  };

  if (!entries || entries.length === 0) {
    return (
      <div
        className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center"
        data-testid="entries-empty"
      >
        <div
          className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
          style={{ background: `${BRAND.teal}12` }}
        >
          <Clock style={{ color: BRAND.teal }} />
        </div>
        <h4 className="mt-3 text-sm font-semibold text-slate-700">No entries yet</h4>
        <p className="mt-1 text-xs text-slate-500">
          Use the timer or add a manual entry to get started.
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
        data-testid="entries-table"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <th className="text-left font-semibold px-4 py-3">Date</th>
                {showUserName && (
                  <th className="text-left font-semibold px-4 py-3">Freelancer</th>
                )}
                <th className="text-left font-semibold px-4 py-3">Project</th>
                <th className="text-left font-semibold px-4 py-3">Activity</th>
                <th className="text-right font-semibold px-4 py-3">Hours</th>
                <th className="text-center font-semibold px-4 py-3">Source</th>
                <th className="text-center font-semibold px-4 py-3">Status</th>
                <th className="text-right font-semibold px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr
                  key={e.id}
                  className="border-t border-slate-100 hover:bg-slate-50/50 transition"
                  data-testid={`entry-row-${e.id}`}
                >
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    {formatFriendlyDate(e.date)}
                  </td>
                  {showUserName && (
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {e.user_name || "—"}
                    </td>
                  )}
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                    {e.project_name ? (
                      <span
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full"
                        style={{
                          background: `${e.project_color || "#94a3b8"}20`,
                          color: e.project_color || "#475569",
                        }}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ background: e.project_color || "#94a3b8" }}
                        />
                        {e.project_name}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-800">{e.activity}</td>
                  <td
                    className="px-4 py-3 text-right font-semibold tabular-nums"
                    style={{ color: BRAND.teal }}
                  >
                    {formatHours(e.hours)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{
                        background:
                          e.source === "timer"
                            ? `${BRAND.teal}15`
                            : `${BRAND.purple}15`,
                        color: e.source === "timer" ? BRAND.teal : BRAND.purple,
                      }}
                    >
                      {e.source === "timer" ? (
                        <Clock size={10} />
                      ) : (
                        <Edit3 size={10} />
                      )}
                      {e.source}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge entry={e} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {readOnly && (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                      {!readOnly && isEntrySubmittable(e) && (
                        <button
                          onClick={() => handleSubmit(e)}
                          data-testid={`submit-entry-${e.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border transition"
                          style={{
                            color: BRAND.teal,
                            borderColor: `${BRAND.teal}55`,
                            background: `${BRAND.teal}10`,
                          }}
                          title="Submit for admin review"
                        >
                          <Send size={12} />
                          Submit
                        </button>
                      )}
                      {!readOnly && isEntryEditable(e) && (
                        <>
                          <button
                            onClick={() => setEditing(e)}
                            data-testid={`edit-entry-${e.id}`}
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded transition"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(e)}
                            data-testid={`delete-entry-${e.id}`}
                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete (5s undo)"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                      {!readOnly && !isEntryEditable(e) && !isEntrySubmittable(e) && (
                        <span className="text-xs text-slate-400">Locked</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <EditEntryDialog
        entry={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          onChanged?.();
        }}
        previewMode={previewMode}
        onPreviewSave={(updates) => {
          if (editing) onPreviewUpdate?.(editing.id, updates);
          setEditing(null);
        }}
      />
    </>
  );
}
